<template>
    <a-modal :title="props.tableColumnsData.full_name + '_发票'" :confirmLoading="state.loading" centered
        :bodyStyle="{ padding: '20px' }" :width="1000" :open="true" @cancel="cancel">
        <template #footer>
            <a-button @click="cancel">取消</a-button>
            <a-popconfirm title="是否确定？" @confirm="onFormSubmit">
                <a-button :loading="state.loading" type="primary">确定申请发票</a-button>
            </a-popconfirm>
        </template>
        <!-- 申请发票记录 -->
        <a-table size="small" :loading="state.loading" style="margin-bottom: 30px" :columns="columns"
            :data-source="dataSource" :pagination="false"
            :row-selection="{ selectedRowKeys: state.selectedRowKeys, type: 'radio', onChange: onSelectChange }"
            rowKey="id">
            <template #bodyCell="{ column, record, index, text }">
                <template v-if="column.dataIndex === 'action'"> {{ record.action_name }} </template>
            </template>
        </a-table>

        <!-- 是否多公司申请发票 -->
        <a-form-item label="是否多公司申请发票" v-if="state.selectedRecord.id && isMultiCompanyOrder">
            <a-radio-group v-model:value="state.isMultiCompany" button-style="solid" @change="onMultiCompanyChange">
                <a-radio :value="1">是</a-radio>
                <a-radio :value="2">否</a-radio>
            </a-radio-group>
            <span style="color: #999; font-size: 12px; margin-left: 8px;">
                该订单为多公司付款，开启后会把一起付款的公司信息写入发票备注
            </span>
        </a-form-item>

        <!-- 多公司信息预览（只读） -->
        <template v-if="state.isMultiCompany === 1 && isMultiCompanyOrder">
            <a-alert type="info" show-icon style="margin-bottom: 12px;"
                :message="`本订单共 ${multiCompanyList.length} 家公司一起付款，将写入同一张发票的备注：`" />
            <div class="multi-invoice-wrapper">
                <div class="multi-invoice-row multi-invoice-header">
                    <div class="col-company">公司名称</div>
                    <div class="col-money">付款金额(元)</div>
                </div>
                <div class="multi-invoice-row" v-for="item in multiCompanyList" :key="item.order_id">
                    <div class="col-company" :title="item.full_name">{{ item.full_name }}</div>
                    <div class="col-money red">￥{{ Number(item.money || 0).toFixed(2) }}</div>
                </div>
                <div class="multi-invoice-row multi-invoice-footer">
                    <div class="col-company">合计</div>
                    <div class="col-money red">￥{{ totalMultiMoney.toFixed(2) }}</div>
                </div>
            </div>
        </template>

        <a-form ref="ruleForm" :rules="rules" :model="formState" v-bind="formLayout" scrollToFirstError="true">
            <a-form-item label="发票抬头名称" name="full_name" required>
                <a-input placeholder="" v-model:value="formState.full_name" @blur="() => {
                    formState.full_name = formState.full_name
                        .replace(/([\u4e00-\u9fa5])\(/g, '$1（')
                        .replace(/\)([\u4e00-\u9fa5])/g, '）$1');
                }"></a-input>
            </a-form-item>
            <a-form-item label="发票抬头税号（统一社会信用代码）" name="social_credit_code" required>
                <a-input placeholder="" v-model:value="formState.social_credit_code"></a-input>
            </a-form-item>
            <a-form-item label="开票税目" required>
                <a-radio-group v-model:value="taxtype">
                    <a-radio :value="1" @click="() => handleRadioChange(1)">入驻服务费</a-radio>
                    <a-radio :value="2" @click="() => handleRadioChange(2)" v-if="state.push_system == 2">房租</a-radio>
                </a-radio-group>
            </a-form-item>
            <a-form-item label="发票备注" name="invoice_remark">
                <a-textarea placeholder="" v-model:value="formState.invoice_remark" :rows="5" />
                西安税务老师一般不要发票，如果需要，入驻服务费即可，请做好解释工作！
            </a-form-item>
            <a-form-item label="开票金额" name="invoice_amount" required>
                <a-input placeholder="" v-model:value="formState.invoice_amount" prefix="￥" suffix="元"
                    style="width: 140px" disabled></a-input>
                <span style="color: #999; font-size: 12px; margin-left: 8px;">（流水金额，不可修改）</span>
            </a-form-item>
            <a-form-item label="申请备注" name="remark">
                <a-textarea placeholder="" v-model:value="formState.remark" />
            </a-form-item>
        </a-form>
    </a-modal>
</template>

<script lang="ts" setup>
import { message } from 'ant-design-vue'
import baseService from '@/utils/http/axios'
import { reactive, onMounted, ref, watch, computed } from 'vue'
import dayjs from 'dayjs'
import Upload from '@/components/base/Upload.vue'

const emit = defineEmits(['cancel', 'ok'])
const props = defineProps({
    tableColumnsData: {
        type: Object,
        default: () => { }
    }
})

const state = reactive<any>({
    loading: false,
    selectedRowKeys: [],
    selectedRecord: {},
    platform_name: '',
    platform_social_credit_code: '',
    push_system: 1,
    lastaddfield: '',
    // 是否多公司申请：1 是，2 否
    isMultiCompany: 2
})

const taxtype = ref<number | undefined>()
const dataSource = ref([])

const columns: Array<Object> = [
    {
        title: '订单号',
        dataIndex: 'order_number',
        width: 250
    },
    {
        title: '流水金额',
        dataIndex: 'actually_paid_money',
        width: 200
    },
    {
        title: '状态',
        dataIndex: 'status',
        width: 200
    },
    {
        title: '类型',
        dataIndex: 'action',
        width: 200
    },
    {
        title: '付款时间',
        dataIndex: 'created_at',
        width: 200
    }
]

// 多公司列表（只用 order_id 作为唯一标识）
interface MultiCompanyItem {
    order_id: number
    full_name: string
    money: number
    before_enter_endtime: string
    alter_enter_endtime: string
}

// 原始多公司列表（包含当前订单 + 后端返回的其他订单，作为判断依据，不随用户切换清空）
const rawMultiCompanyList = ref<MultiCompanyItem[]>([])
// 展示用多公司列表（用户选“否”时清空）
const multiCompanyList = ref<MultiCompanyItem[]>([])

// 是否多公司付款订单（rawMultiCompanyList 已包含当前订单，> 1 即多公司）
const isMultiCompanyOrder = computed(() => {
    return rawMultiCompanyList.value.length > 1
})

onMounted(() => {
    loadData()
})

watch(taxtype, () => {
    recalcAmount()
    refreshRemark()
})

const loadData = () => {
    state.loading = true
    // 重置选中和多公司状态
    state.selectedRecord = {}
    state.selectedRowKeys = []
    state.isMultiCompany = 2
    rawMultiCompanyList.value = []
    multiCompanyList.value = []

    let requestParam = {
        url: '/admin/v1/customerInvoiceRecord',
        method: 'post',
        data: { customer_id: props.tableColumnsData.id }
    }
    baseService(requestParam)
        .then((res: any) => {
            state.loading = false
            dataSource.value = res.data.list
            formState.full_name = res.data.full_name
            formState.social_credit_code = res.data.social_credit_code
            state.platform_name = res.data.platform_name
            state.platform_social_credit_code = res.data.platform_social_credit_code
            state.push_system = res.data.push_system
            if (state.push_system != 2) {
                handleRadioChange(1)
            }
        })
        .catch((res) => {
            state.loading = false
            if (res.msg) {
                message.error(res.msg)
            } else {
                message.error('查询失败')
            }
        })
}

// 基础备注（不含多公司）
const baseInvoiceRemark = () => {
    const record = state.selectedRecord
    if (!record?.action) return ''
    if (record.action === 'custRenewal' || record.action === 'custRegister') {
        return record.full_name +
            dayjs(record.before_enter_endtime, 'YYYY年MM月DD日').format('YYYY年MM月') +
            '~' +
            dayjs(record.alter_enter_endtime, 'YYYY年MM月DD日').format('YYYY年MM月') +
            state.lastaddfield
    }
    return ''
}

// 多公司备注文本
const multiCompanyRemarkText = () => {
    if (state.isMultiCompany !== 1 || !isMultiCompanyOrder.value) return ''
    console.log(multiCompanyList)
    const lines = multiCompanyList.value.map((item: any) => {
        let range = dayjs(item.before_enter_endtime, 'YYYY年MM月DD日').format('YYYY年MM月') +
            '~' +
            dayjs(item.alter_enter_endtime, 'YYYY年MM月DD日').format('YYYY年MM月') + state.lastaddfield
        return `${item.full_name} ${range} ￥${Number(item.money || 0).toFixed(2)}元`
    })
    return lines.join('\n')
}

// 统一刷新备注
const refreshRemark = () => {
    if (state.isMultiCompany == 2) {
        formState.invoice_remark = baseInvoiceRemark()
    } else {
        formState.invoice_remark = multiCompanyRemarkText()
    }
}

const handleRadioChange = (val: number) => {
    taxtype.value = val
    state.lastaddfield = val === 1 ? '入驻服务费' : '房租'
    refreshRemark()
}

// 根据当前列表（单公司或多公司）重算开票金额
const recalcAmount = () => {
    const list = state.isMultiCompany === 1 && multiCompanyList.value.length > 0
        ? multiCompanyList.value
        : [{
            order_id: state.selectedRecord?.id ?? 0,
            full_name: state.selectedRecord?.full_name ?? '',
            money: Number(state.selectedRecord?.actually_paid_money ?? 0)
        }]

    // 合计流水金额
    const totalAmount = list.reduce((sum, item) => sum + Number(item.money || 0), 0)

    formState.invoice_amount = Number(totalAmount.toFixed(2))
}

// 选中表格行
const onSelectChange = (selectedRowKeys: any[], val) => {
    state.selectedRowKeys = selectedRowKeys
    state.selectedRecord = val[0]

    taxtype.value = undefined
    state.lastaddfield = ''

    // 后端返回的是“多出来的其他订单”
    const others = val[0].multi_company_list || []

    // 把当前订单拼进去，组成完整列表
    const currentItem: MultiCompanyItem = {
        order_id: val[0].id ?? 0,
        full_name: val[0].full_name ?? props.tableColumnsData.full_name ?? '',
        money: Number(val[0].actually_paid_money ?? 0),
        before_enter_endtime: val[0].before_enter_endtime ?? '',
        alter_enter_endtime: val[0].alter_enter_endtime ?? ''
    }

    const fullList: MultiCompanyItem[] = [
        currentItem,
        ...others.map((item: any) => ({
            order_id: item.order_id ?? item.id ?? 0,
            full_name: item.full_name ?? '',
            money: Number(item.money ?? item.actually_paid_money ?? 0),
            before_enter_endtime: item.before_enter_endtime ?? '',
            alter_enter_endtime: item.alter_enter_endtime ?? ''
        }))
    ]

    rawMultiCompanyList.value = fullList

    if (rawMultiCompanyList.value.length > 1) {
        state.isMultiCompany = 1
        multiCompanyList.value = [...rawMultiCompanyList.value]
    } else {
        state.isMultiCompany = 2
        multiCompanyList.value = []
    }

    recalcAmount()
    refreshRemark()
}

// 多公司开关
const onMultiCompanyChange = () => {
    if (state.isMultiCompany === 1) {
        multiCompanyList.value = [...rawMultiCompanyList.value]
    } else {
        multiCompanyList.value = []
    }
    recalcAmount()
    refreshRemark()
}

// 多公司合计
const totalMultiMoney = computed(() => {
    return multiCompanyList.value.reduce((sum, item) => sum + Number(item.money || 0), 0)
})

// 表单配置
const formLayout = {
    labelCol: {
        xs: { span: 7 },
        sm: { span: 7 }
    },
    wrapperCol: {
        xs: { span: 14 },
        sm: { span: 14 }
    }
}
const ruleForm = ref()
const rules = {
    full_name: [{ required: true, message: '请输入发票抬头名称', trigger: 'blur' }],
    social_credit_code: [{ required: true, message: '请输入统一社会信用代码', trigger: 'blur' }]
}
const formState = reactive({
    full_name: '',
    social_credit_code: '',
    fee_standard: 1,
    invoice_amount: 0,
    invoice_remark: '',
    attachment: [],
    remark: '',
    order_id: 0,
    customer_id: 0
})

const onFormSubmit = () => {
    ruleForm.value
        .validate()
        .then(() => {
            if (!state.selectedRecord.id) {
                message.error('请选择开票订单')
                return
            }
            if (!taxtype.value) {
                message.error('请选择开票税目')
                return
            }
            if (formState.invoice_amount <= 0) {
                message.error('开票金额无效')
                return
            }
            // 多公司时必须有明细
            if (state.isMultiCompany === 1 && isMultiCompanyOrder.value) {
                if (multiCompanyList.value.length === 0) {
                    message.error('多公司付款明细为空')
                    return
                }
            }

            state.loading = true
            formState.order_id = state.selectedRecord.id
            formState.customer_id = props.tableColumnsData.id

            // 提交数据
            let submitData: any = {
                ...formState,
                tax_type: taxtype.value,
                is_multi_company: state.isMultiCompany,
                multi_company_list: state.isMultiCompany === 1
                    ? multiCompanyList.value
                        .filter(item => item.order_id !== state.selectedRecord.id)
                        .map(item => ({
                            order_id: item.order_id
                        }))
                    : []
            }

            let requestParam = {
                url: '/admin/v1/customerInvoice',
                method: 'post',
                data: submitData
            }
            baseService(requestParam)
                .then(() => {
                    message.success('提交成功')
                    cancel()
                })
                .catch((res) => {
                    state.loading = false
                    message.error(res.msg || '申请失败')
                })
        })
        .catch(() => { })
}

const cancel = () => {
    emit('cancel')
}
</script>

<style lang="scss" scoped>
.multi-invoice-wrapper {
    border: 1px solid #f0f0f0;
    border-radius: 4px;
    padding: 8px 12px;
    background: #fafafa;
    margin-bottom: 16px;
}

.multi-invoice-row {
    display: flex;
    align-items: center;
    padding: 6px 0;
    border-bottom: 1px dashed #eee;
    font-size: 13px;

    &:last-child {
        border-bottom: none;
    }

    >div {
        padding: 0 4px;
        box-sizing: border-box;
    }

    .col-company {
        flex: 4;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .col-money {
        flex: 1.5;
        text-align: center;
    }
}

.multi-invoice-header {
    font-weight: bold;
    color: #333;
    background: #f0f5ff;
    border-radius: 4px;
}

.multi-invoice-footer {
    font-weight: bold;
    color: #333;
    background: #fffbe6;
    border-radius: 4px;
    margin-top: 4px;
}

.red {
    color: red;
}
</style>
